// Copyright (c) StrangeBee 2024

import React, { ReactElement, useMemo, useState } from 'react';
import { useLocalMessage } from '@Helpers/';
import UploadSingleFile from '@Atoms/UploadSingleFile/UploadSingleFile';
import { THForm } from '@Molecules/THForm/THForm';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THPassword } from '@Molecules/Inputs/THPassword/THPassword';
import { CaseCreateFormTabs } from './CaseCreateFormTabs';
import { UseValidateFormReturn } from '@Hooks/useValidateForm';
import { useGenericQuery } from '@Queries/genericQueries';
import client from '@Client';
import useOneQuery from '@Hooks/useOneQuery';
import { useCurrentUser } from '@Queries/userQueries';
import { OrganisationOutput, InputShare, ProfileOutput } from 'thehive-sdk';
import { computeGetSelector, computeListSelector } from '@Organisms/Organisation/selector';
import { computeListSelector as computeProfileListSelector } from '@Organisms/Profile/selector';
import useListQuery from '@Hooks/useListQuery';
import { Space } from 'antd';
import { AlertImport } from '@Molecules/AlertImport/AlertImport';
import { CaseCreateFormValues } from './CaseCreateForm';
import { getAvailableLinks } from '@Helpers/caseSharing';

interface CaseCreateImportFormValues {
    archive: string;
    password: string;
    sharingParameters?: InputShare[];
}

interface CaseCreateImportProps {
    onImport: (formData: FormData) => Promise<any>;
    toggleDrawer: () => void;
    caseImportValidationForm: UseValidateFormReturn;
}

export const CaseCreateImport = ({
    caseImportValidationForm,
    onImport,
    toggleDrawer,
}: CaseCreateImportProps): ReactElement => {
    const i = useLocalMessage();
    const [stateLoading, setStateLoading] = useState(false);
    const { data: currentUser } = useCurrentUser();
    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = caseImportValidationForm;

    const onFinish = ({ archive, password, sharingParameters }: CaseCreateImportFormValues): void => {
        const formData = new FormData();

        if (archive) {
            setStateLoading(true);
            formData.set('file', archive as any);
            formData.set('_json', JSON.stringify({ sharingParameters, password }));
            onImport(formData)
                .then(() => {
                    toggleDrawer?.();
                })
                .finally(() => {
                    setStateLoading(false);
                });
        }
    };

    const { data: sharingProfileList = [] } = useGenericQuery({
        fetchCallback: () => client.OrganisationController.getSharingProfile(),
        key: ['get-organisation-sharing-profile'],
        manual: true,
        fetchOnStart: false,
    });

    const { data: currentOrg } = useOneQuery<OrganisationOutput>({
        controller: client.OrganisationController,
        selector: computeGetSelector({ orgId: currentUser?.organisation || '' }),
        manual: true,
        fetchOnStart: false,
    });

    const { data: orgList = [] } = useListQuery<OrganisationOutput[]>({
        queryParams: {
            selector: computeListSelector(),
        },
        controller: client.OrganisationController,
        manual: true,
        fetchOnStart: true,
    });

    const { data: profileList = [] } = useListQuery<ProfileOutput[]>({
        queryParams: {
            selector: computeProfileListSelector(),
        },
        controller: client.ProfileController,
        manual: true,
        fetchOnStart: true,
    });

    const orgProfileList = useMemo(() => profileList.filter((profile) => profile.forOrg), [profileList]);

    const availableLinks = useMemo(
        () => currentOrg?.links && getAvailableLinks(currentOrg.links, orgList),
        [currentOrg?.links, orgList],
    );

    const defaultCaseValues: Partial<CaseCreateFormValues> = {
        sharingParameters:
            availableLinks?.map(({ toOrganisation = '', linkType = '' }) => {
                const sharingProfile = sharingProfileList.find(({ name }) => name === linkType);

                return {
                    organisation: toOrganisation,
                    profile: sharingProfile?.permissionProfile,
                    share: sharingProfile?.autoShare,
                    observableRule: sharingProfile?.observableRule,
                    taskRule: sharingProfile?.taskRule,
                    editable: sharingProfile?.editable,
                };
            }) || [],
    };

    return (
        <THForm<CaseCreateImportFormValues>
            name="attachment-form"
            form={form}
            onFinish={(values) => {
                onFinish({ ...(defaultCaseValues || {}), ...values });
                form.resetFields();
                toggleDrawer();
                setFieldsTouched(false);
            }}
            onValuesChange={() => {
                onFormValuesChange();
                setFieldsTouched(true);
            }}
            initialValues={defaultCaseValues || {}}
        >
            <THForm.Layout
                footer={
                    <FormSubmitButton
                        loading={stateLoading}
                        onCancel={() => {
                            form.resetFields();
                            toggleDrawer();
                            setFieldsTouched(false);
                        }}
                        disabled={!isValid || !fieldsTouched}
                        footerText={i(`general.confirm`)}
                    />
                }
            >
                <Space direction="vertical" className="file-import__container" size="large">
                    <AlertImport title={i('case.create.import.archive.title')}>
                        <>{i('case.create.import.archive.content')}</>
                    </AlertImport>
                    <div className="file-import__upload">
                        <span className="file-import__upload-title">{i('general.import.upload.label')}</span>
                        <THForm.Item
                            name="archive"
                            rules={[
                                {
                                    required: true,
                                    message: i('attachment.create.required'),
                                },
                            ]}
                        >
                            <UploadSingleFile text={i('general.import.upload.content')} accept=".thar" />
                        </THForm.Item>

                        <span>{i('case.create.import.archive.instruction')}</span>
                    </div>
                    <THForm.Item
                        label={i('attachment.create.password')}
                        name="password"
                        rules={[
                            {
                                required: true,
                                message: i('attachment.create.field-required'),
                            },
                        ]}
                    >
                        <THPassword />
                    </THForm.Item>
                </Space>

                <CaseCreateFormTabs
                    form={form}
                    onChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    orgList={orgList}
                    orgProfileList={orgProfileList}
                    currentOrg={currentOrg}
                    canManageTasks={false}
                    canManageCustomFields={false}
                />
            </THForm.Layout>
        </THForm>
    );
};
